import * as React from "react";
import * as RechartsPrimitive from "recharts";
import type { TooltipPayloadEntry } from "recharts";
import { cn } from "@/lib/utils";
// import { useCurrentWorkspace } from "@/store/authContext/authContext";
import { getPercentChange } from "@/common/chartDataTransformer";
import { SeriesConfig } from "@/common/type/report-types";
import { formatValueV2 } from "@/common/valueFormatter";
import { useCurrentWorkspace } from "@/store/authContext/authContext";
import { ChartTypeName } from "@/common/report-configuration";
import { compareLagTimeBucketLabels } from "@/common/dataLimiter";

const ChartTooltipV2 = RechartsPrimitive.Tooltip;

type NormalizedTooltipPayloadEntry = {
  dataKey?: string;
  name?: string | number;
  value?: string | number | ReadonlyArray<number | string>;
  color?: string;
  axisId?: string;
  measureName?: string;
  payload?: Record<string, unknown>;
};

const ChartTooltipContentV2 = React.forwardRef<
  HTMLDivElement,
  Omit<React.ComponentProps<typeof RechartsPrimitive.Tooltip>, "content"> &
    React.ComponentProps<"div"> & {
      hideLabel?: boolean;
      hideIndicator?: boolean;
      nameKey?: string;
      labelKey?: string;
      seriesConfig?: SeriesConfig;
      active?: boolean;
      payload?: readonly NormalizedTooltipPayloadEntry[];
      label?: string | number;
      showCount?: boolean;
    }
>(
  (
    {
      active,
      payload,
      className,
      hideLabel = false,
      label,
      labelClassName,
      color,
      seriesConfig,
      showCount = true,
    },
    ref,
  ) => {
    const workspace = useCurrentWorkspace();

    // Helper function to determine if employee count should be shown
    const shouldShowEmployeeCount = React.useCallback(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (item: any) => {
        // Use pre-computed metadata from seriesConfig
        const seriesItem = seriesConfig?.get(item.dataKey);
        if (!seriesItem?.tooltip) {
          return false;
        }

        return (
          seriesItem.tooltip.showEmployeeCount &&
          seriesItem.tooltip.hasEmployeeData &&
          item.payload[`${item.dataKey}_employees`] !== undefined &&
          item.payload[`${item.dataKey}_employees`] !== item.value
        );
      },
      [seriesConfig],
    );

    const tooltipLabel = React.useMemo(() => {
      if (hideLabel || !payload?.length) {
        return null;
      }

      const [item] = payload;

      return (
        <div
          className={cn(
            "font-medium flex items-center justify-between gap-4",
            labelClassName,
          )}
        >
          <span>{label}</span>
          {shouldShowEmployeeCount(item) && (
            <span className="text-muted-foreground font-normal shrink-0">
              (# employees)
            </span>
          )}
        </div>
      );
    }, [label, payload, hideLabel, labelClassName, shouldShowEmployeeCount]);

    if (!active || !payload?.length) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          "grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl",
          className,
        )}
      >
        {tooltipLabel}
        <div className="grid gap-1.5">
          {payload.map((item) => {
            const indicatorColor = color || item.payload?.fill || item.color;
            const dataKey = item.dataKey;
            if (!dataKey) {
              return false;
            }

            const seriesItem = seriesConfig?.get(dataKey);
            if (!seriesItem?.tooltip) {
              return false;
            }

            const previousSeriesKey = `${dataKey}_previous`;
            const hasComparisonPair =
              seriesItem.isPreviousPeriod ||
              seriesConfig?.has(previousSeriesKey);
            const hasPreviousSeries =
              !seriesItem.isPreviousPeriod &&
              seriesConfig?.has(previousSeriesKey);
            const previousItem = payload.find(
              (candidate) => candidate.dataKey === previousSeriesKey,
            );
            const previousValue =
              item.payload?.[previousSeriesKey] ?? previousItem?.value;
            const percentChange = hasPreviousSeries
              ? getPercentChange(item.value, previousValue)
              : undefined;
            const formattedValue =
              item.value === undefined || item.value === null
                ? undefined
                : showCount
                  ? formatValueV2(
                      item.value as string,
                      seriesItem.measure,
                      workspace,
                    )
                  : `${parseFloat(Number(item.value).toFixed(1))}%`;
            const employeeCount = shouldShowEmployeeCount(item)
              ? (item.payload?.[`${dataKey}_employees`] as React.ReactNode)
              : undefined;
            const percentChangeText =
              percentChange === undefined
                ? ""
                : percentChange === null
                  ? "—"
                  : `(${percentChange > 0 ? "+" : ""}${percentChange.toFixed(1)}%)`;
            const percentChangeColor =
              percentChange === null ||
              percentChange === undefined ||
              percentChange === 0
                ? "text-muted-foreground"
                : percentChange > 0
                  ? "text-green-600"
                  : "text-red-600";

            return (
              <div
                key={dataKey}
                className={cn(
                  "flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground items-center",
                )}
              >
                <>
                  <div
                    className={cn(
                      "shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg] h-2.5 w-2.5",
                    )}
                    style={
                      {
                        "--color-bg": indicatorColor,
                        "--color-border": indicatorColor,
                      } as React.CSSProperties
                    }
                  />
                  <div
                    className={cn(
                      "grid min-w-0 flex-1 items-center gap-x-1 leading-none",
                      hasComparisonPair
                        ? "grid-cols-[minmax(0,1fr)_4rem_3.5rem]"
                        : "grid-cols-[minmax(0,1fr)_auto]",
                    )}
                  >
                    <div className="grid min-w-0 gap-1.5">
                      <span className="text-muted-foreground">{item.name}</span>
                    </div>
                    {formattedValue !== undefined ? (
                      <span className="text-right font-mono font-medium tabular-nums text-foreground">
                        {formattedValue}
                        {employeeCount !== undefined && (
                          <span className="text-muted-foreground">
                            {` (${employeeCount})`}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span />
                    )}
                    {hasComparisonPair && (
                      <span
                        className={cn(
                          "text-right font-mono font-medium tabular-nums",
                          percentChangeColor,
                        )}
                      >
                        {percentChangeText}
                      </span>
                    )}
                  </div>
                </>
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);
ChartTooltipContentV2.displayName = "ChartTooltip";

// Helper function to deduplicate projected data in line charts
function deduplicateProjectedData(
  payload: readonly TooltipPayloadEntry[],
  seriesConfig: SeriesConfig,
): NormalizedTooltipPayloadEntry[] {
  const valueMap = new Map<
    string,
    {
      value: number;
      employees?: number;
      isProjected?: boolean;
    }
  >();

  // First pass: collect all values and identify overlap points
  const hasActualValue = new Set<string>();
  payload.forEach((entry) => {
    const key = typeof entry.dataKey === "string" ? entry.dataKey : undefined;
    if (!key) {
      return;
    }

    const isProjected = key.endsWith("_projected");
    const baseKey = key.replace("_projected", "");

    if (!isProjected) {
      hasActualValue.add(baseKey);
    }

    if (
      !valueMap.has(baseKey) ||
      (isProjected && !hasActualValue.has(baseKey))
    ) {
      // Only use projected values if there's no actual value for this point
      valueMap.set(baseKey, {
        value: Number(entry.value) || 0,
        employees: !isProjected
          ? Number(entry.payload?.[`${key}_employees`])
          : undefined,
        isProjected,
      });
    }
  });

  // Convert to tooltip payload format
  return Array.from(valueMap.entries())
    .map(([key, values]) => {
      const seriesItem = seriesConfig.get(key);

      return {
        dataKey: values.isProjected ? `${key}_projected` : key,
        measureName: seriesItem?.measure.measure || "",
        name:
          values.isProjected && !hasActualValue.has(key)
            ? `${seriesItem?.label || key} (Projected)`
            : seriesItem?.label || key,
        value: values.value,
        color: seriesItem?.color || "#000",
        axisId: seriesItem?.axisId || "", // used to determine value formatting
        payload: {
          [values.isProjected ? `${key}_projected` : key]: values.value,
          [`${key}_employees`]: values.employees,
        },
      };
    })
    .sort((a, b) => {
      // if one or the other item.measureName === "SURVEY_SCORE" (but not both), sort that one first.
      if (
        a.measureName === "SURVEY_SCORE" &&
        b.measureName !== "SURVEY_SCORE"
      ) {
        return -1;
      }
      if (
        b.measureName === "SURVEY_SCORE" &&
        a.measureName !== "SURVEY_SCORE"
      ) {
        return 1;
      }
      // if both are SURVEY_SCORE, sort by item.dataKey, this preserves the likert scale order.
      if (
        a.measureName === "SURVEY_SCORE" &&
        b.measureName === "SURVEY_SCORE"
      ) {
        return b.dataKey.localeCompare(a.dataKey);
      }

      // Keep custom bucket ordering aligned with the underlying chart data sorting.
      const aLabel = typeof a.dataKey === "string" ? a.dataKey : "";
      const bLabel = typeof b.dataKey === "string" ? b.dataKey : "";
      const lagOrder = compareLagTimeBucketLabels(aLabel, bLabel);
      if (lagOrder !== 0) {
        return lagOrder;
      }

      return b.value - a.value;
    });
}

// Chart type aware tooltip wrapper
interface ChartTooltipV2WrapperProps {
  chartType: ChartTypeName;
  seriesConfig: SeriesConfig;
  active?: boolean;
  payload?: readonly TooltipPayloadEntry[];
  label?: string | number;
  showCount?: boolean;
}

const ChartTooltipV2Wrapper = React.memo<ChartTooltipV2WrapperProps>(
  ({ chartType, seriesConfig, active, payload, label, showCount = true }) => {
    // Early exit
    if (!active || !payload?.length) return null;

    let processedPayload: readonly NormalizedTooltipPayloadEntry[] =
      payload.map((entry) => ({
        ...entry,
        dataKey: typeof entry.dataKey === "string" ? entry.dataKey : undefined,
        payload: (entry.payload ?? {}) as Record<string, unknown>,
      }));

    // Apply chart-specific transformations
    switch (chartType) {
      case "Pie": {
        // PIE charts have a different payload structure - need to reshape
        const pieData = payload[0];
        const pieName =
          typeof pieData.name === "string" || typeof pieData.name === "number"
            ? pieData.name
            : undefined;
        processedPayload = [
          {
            dataKey: typeof pieName === "string" ? pieName : undefined,
            name: pieName,
            value: pieData.value,
            color: pieData.payload?.color,
            payload: (pieData.payload ?? {}) as Record<string, unknown>,
            axisId:
              typeof pieName === "string"
                ? seriesConfig.get(pieName)?.axisId || ""
                : "",
          },
        ];
        break;
      }

      case "Line":
        // LINE charts with projected data need deduplication
        processedPayload = deduplicateProjectedData(payload, seriesConfig);
        break;

      case "Bar":
      case "Horizontal Bar":
      case "Funnel":
        {
          // Inject axisId for bar charts (Recharts doesn't pass it automatically)
          const firstAxisId =
            Array.from(seriesConfig.values())[0]?.axisId || "";
          const normalizedPayload = payload.map((entry) => {
            const normalizedEntry = {
              ...entry,
              dataKey:
                typeof entry.dataKey === "string" ? entry.dataKey : undefined,
              payload: (entry.payload ?? {}) as Record<string, unknown>,
              axisId: firstAxisId,
            };
            // Clean up projected labels
            if (typeof normalizedEntry.name === "string") {
              normalizedEntry.name = normalizedEntry.name.replace(
                "_projected",
                " (Projected)",
              );
            }
            return normalizedEntry;
          });
          // Deduplicate if there's projected data
          processedPayload = deduplicateProjectedData(
            normalizedPayload,
            seriesConfig,
          );
        }
        break;

      default:
        // For other chart types, add axisId from seriesConfig if missing
        processedPayload = payload.map((entry) => ({
          ...entry,
          dataKey:
            typeof entry.dataKey === "string" ? entry.dataKey : undefined,
          payload: (entry.payload ?? {}) as Record<string, unknown>,
          axisId:
            typeof entry.dataKey === "string"
              ? seriesConfig.get(entry.dataKey)?.axisId || ""
              : "",
        }));
        break;
    }

    return (
      <ChartTooltipContentV2
        active={active}
        payload={processedPayload}
        label={label}
        seriesConfig={seriesConfig}
        showCount={showCount}
      />
    );
  },
);
ChartTooltipV2Wrapper.displayName = "ChartTooltipV2Wrapper";

export { ChartTooltipV2, ChartTooltipV2Wrapper };
